<template>
  <base-slide-over ref="slideOver" :title="title" size="2xl">
    <base-form ref="form" class="tw-py-2.5 tw-px-4 sm:tw-py-4 sm:tw-px-6" v-model="isFormValid">
      <base-text-field label="Full Name" placeholder="Enter full name" v-model="fullName" class="tw-mt-3" required />
      <div class="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 tw-gap-x-4">
        <base-email-field label="Email" placeholder="Enter email" v-model="email" required />
        <base-phone-number-input label="Phone" placeholder="Enter phone" v-model="mobile" :e164.sync="phone164" required />
      </div>
      <base-advanced-select label="Plan" placeholder="Select plan" :options="planOptions" v-model="selectedPlans" label-key="name" multiple required />
      <base-select
        label="Sign Up Location"
        placeholder="Select a location"
        :optgroups="locationOptGroups"
        v-model="selectedLocationId"
        label-key="name"
        value-key="businessLocationId"
        required
        v-if="locations.length > 1"
      />
    </base-form>
    <template #fixed-bottom>
      <div class="tw-py-2 tw-px-3 tw-flex tw-justify-end">
        <base-button type="flat" @click="$refs.slideOver.hide()" class="tw-mr-3">Cancel</base-button>
        <base-button @click="inviteToPlan" :disabled="!isFormValid" :loading="isLoading">Invite</base-button>
      </div>
    </template>
  </base-slide-over>
</template>

<script>
import { mapGetters } from "vuex";
import { capitalize, uniq, orderBy } from "lodash";
import { STATE_OPTIONS } from "@/constants/constants";

export default {
  props: {
    businessId: String,
  },
  data() {
    return {
      isFormValid: true,
      isLoading: false,
      isCreatingPlan: false,
      fullName: "",
      email: "",
      mobile: "",
      phone164: "",
      isLoadingPlans: false,
      plans: [],
      selectedPlans: [],
      selectedLocationId: null,
    };
  },
  computed: {
    ...mapGetters("organization", ["organization"]),
    ...mapGetters("location", ["location", "locations"]),
    title() {
      return `Invite ${capitalize(this.organization.customerLabelSingular)} to Plan`;
    },
    planGroups() {
      return this.plans ? uniq(this.plans.map(({ planGroup }) => planGroup)).sort() : [""];
    },
    planOptions() {
      const planOptions = [];

      this.planGroups.forEach((pg) => {
        planOptions.push({
          name: pg,
          isHeader: true,
        });

        planOptions.push(...orderBy(this.plans.filter((p) => p.planGroup === pg), ["name"]));
      });

      return planOptions;
    },
    locationOptGroups() {
      return STATE_OPTIONS.filter((s) => this.locations.find((l) => l.state === s.value)).map((s) => ({
        label: s.value,
        options: this.locations.filter((l) => l.state === s.value),
      }));
    },
  },
  created() {
    this.getPlans();
  },
  mounted() {
    if (this.locations.length === 1) this.selectedLocationId = this.locations[0].businessLocationId;
  },
  methods: {
    show() {
      this.$refs.slideOver.show();
      this.$refs.form.reset();
    },
    hide() {
      this.$refs.slideOver.hide();
    },
    async getPlans() {
      try {
        this.isLoadingPlans = true;
        const response = await this.$axios.get(`/businesses/${this.businessId}/plans/list`);
        this.plans = response.data;
      } catch (error) {
        this.EventBus.$emit("GLOBAL_ERROR", {
          title: "Unable to retrieve plans",
          message: "We were not able to retrieve the your business plans. There may have been a connection error. Please reload the page to try again.",
          error,
        });
      } finally {
        this.isLoadingPlans = false;
      }
    },
    async inviteToPlan() {
      try {
        if (!this.$refs.form.validate()) return;
        this.isLoading = true;
        await this.$axios.post(`/businesses/${this.businessId}/member-invite`, {
          fullName: this.fullName,
          email: this.email,
          phone: this.phone164,
          businessLocationId: this.selectedLocationId,
          businessPlanIds: this.selectedPlans.map((sp) => sp.businessPlanId),
        });
        this.EventBus.$emit("GLOBAL_NOTIFY", { title: "Member invited", message: "The member was invited" });
        this.$emit("invited");
        this.hide();
      } catch (error) {
        this.EventBus.$emit("GLOBAL_ERROR", {
          title: "Failed to invite user",
          message: "We were not able to invite " + this.fullName + ". Please try again.",
          error,
        });
      } finally {
        this.isLoading = false;
      }
    },
  },
};
</script>
